import { useEffect, useMemo } from "react"

import { Col, Flex, Row, Spin, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import Button from "../../../Components/Button"
import EmptyComponent from "../../../Components/Empty"
import InfoTooltip from "../../../Components/InfoTooltip/InfoTooltip"
import Table from "../../../Components/Table"
import { skeletonColumns, skeletonRows } from "../../../Components/Table/skeleton"
import { RoutesPaths } from "../../../constants/user-routes"
import Icon from "../../../Icon"
import { MATCH_RATE_SUMMARY_CELLS, matchRateColumns } from "../components/channelColumns"
import DeflectFilters from "../components/DeflectFilters"
import MatchRateGrid from "../components/MatchRateGrid/MatchRateGrid"
import MatchRateGridSkeleton from "../components/MatchRateGrid/MatchRateGridSkeleton"
import SummaryRows from "../components/SummaryRows/SummaryRows"
import { useDeflectScope } from "../hooks/useDeflectScope"
import { DEFLECT_SECTION } from "../redux/deflectAnalytics.constants"
import {
  selectDeflectFetching,
  selectDeflectFirstLoad,
  selectDeflectSection,
  selectProgramCount
} from "../redux/deflectAnalytics.slice"
import { toMatchRateGrid } from "../utils/matchRateGrid"
import { CHANNEL_COUNT } from "../utils/vocabulary"

import styles from "../analytics.module.less"
import tables from "../tables.module.less"

const { Title } = Typography

// The Match rate page: the card's heat map full width, then the exact figures per channel. The
// heat map is the card's own answer; the table is by-channel/, the donut's.
const MatchRateFullPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { getBase, getCompare } = useDeflectScope()
  const matrix = useSelector(selectDeflectSection(DEFLECT_SECTION.MATCH_RATE_MATRIX))
  const matrixFetching = useSelector(selectDeflectFetching(DEFLECT_SECTION.MATCH_RATE_MATRIX))
  const matrixFirstLoad = useSelector(selectDeflectFirstLoad(DEFLECT_SECTION.MATCH_RATE_MATRIX))
  const programCount = useSelector(selectProgramCount)
  const byChannel = useSelector(selectDeflectSection(DEFLECT_SECTION.BY_CHANNEL))
  const byChannelFetching = useSelector(selectDeflectFetching(DEFLECT_SECTION.BY_CHANNEL))
  const byChannelFirstLoad = useSelector(selectDeflectFirstLoad(DEFLECT_SECTION.BY_CHANNEL))

  useEffect(() => {
    getBase(DEFLECT_SECTION.MATCH_RATE_MATRIX)
  }, [getBase])

  useEffect(() => {
    getCompare(DEFLECT_SECTION.BY_CHANNEL)
  }, [getCompare])

  const { cols, rows: matrixRows } = useMemo(() => toMatchRateGrid(matrix), [matrix])
  const tableRows = byChannel?.result ?? []
  const columns = useMemo(() => matchRateColumns(t), [t])
  const loadingColumns = useMemo(() => skeletonColumns(columns), [columns])

  useEffect(() => {
    document.title = "Match rate by channel and program - Chargeback"
    document.getElementById("global-wrap")?.scrollTo(0, 0)
  }, [])

  return (
    <div className={styles.page}>
      <Row gutter={[32, 32]}>
        <Col span={24}>
          <Button
            type="text"
            onClick={() => navigate(RoutesPaths.deflect.analytics)}
            icon={<Icon type="chevron_left_small" />}
            style={{ marginLeft: -12 }}
            text={t("Back to Analytics")}
          />
        </Col>

        <Col span={24}>
          <Flex justify="space-between" align="center" gap={12} wrap="wrap">
            <Flex align="center" gap={8}>
              <Title level={4} style={{ marginBottom: 0 }}>
                {t("Match rate: channel × program")}
              </Title>
              <InfoTooltip
                title={t(
                  "Match rate for each channel and card-network program, over the lookups served while that program was Active. Mastercom exists on Consumer Clarity only."
                )}
                placement="top"
              />
            </Flex>
            <DeflectFilters />
          </Flex>
        </Col>

        <Col span={24}>
          {matrixFirstLoad ? (
            <div className={styles.matchRateFull}>
              <MatchRateGridSkeleton programCount={programCount} />
            </div>
          ) : (
            <Spin spinning={matrixFetching}>
              {matrixRows.length ? (
                <div className={styles.matchRateFull}>
                  <MatchRateGrid cols={cols} rows={matrixRows} />
                </div>
              ) : (
                <EmptyComponent type="no_data_available" style={{ minHeight: 450 }} />
              )}
            </Spin>
          )}
        </Col>

        <Col span={24}>
          <Table
            className={tables.olympics}
            dataSource={byChannelFirstLoad ? skeletonRows(CHANNEL_COUNT) : tableRows}
            columns={byChannelFirstLoad ? loadingColumns : columns}
            loading={byChannelFetching && !byChannelFirstLoad}
            withOutPagination
            scroll={{}}
            rowKey={byChannelFirstLoad ? "key" : "channel"}
            tableLayout="fixed"
            showHeader={byChannelFirstLoad || !!tableRows.length}
            summary={() => (
              <SummaryRows
                summary={tableRows.length && !byChannelFirstLoad ? byChannel?.summary : null}
                comparisonSummary={byChannel?.comparison_summary}
                comparisonLabel={byChannel?.comparison_period?.label}
                cells={MATCH_RATE_SUMMARY_CELLS}
              />
            )}
            locale={{ emptyText: <EmptyComponent type="no_data_available" /> }}
          />
        </Col>
      </Row>
    </div>
  )
}

export default MatchRateFullPage
